import { z } from 'zod';
import { LEAVE_TYPES, OT_TYPES, ROLES } from '../types/index.js';

// Re-export integration schemas (JobPilot ↔ PayPilot — see HANDOFF-CONTRACT.md)
export * from './integrations.js';

// === Reusable primitives ===
export const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'รูปแบบวันที่ต้องเป็น YYYY-MM-DD');
export const timeString = z.string().regex(/^\d{2}:\d{2}$/, 'รูปแบบเวลาต้องเป็น HH:mm');
export const periodString = z.string().regex(/^\d{4}-\d{2}$/, 'รูปแบบงวดต้องเป็น YYYY-MM');
export const empCodeString = z.string().min(1).max(20);

/** ⭐ ฟอร์มฝั่งเว็บแปลงค่า null ในฐานข้อมูลเป็น '' เพื่อให้ช่องกรอกทำงานได้
 *  แล้วส่งกลับมาทั้งก้อน — ฟิลด์ข้อความรับ '' ได้ผ่าน `.or(z.literal(''))`
 *  แต่ฟิลด์ตัวเลข/บูลีน/ตัวเลือกรับไม่ได้ จึงตีความ '' = "ไม่ได้ส่งมา"
 *  (undefined) = ไม่แตะค่าเดิม แทนที่จะตอบ 400 ทั้งฟอร์ม.
 *  เคยพัง: พนักงานรายเดือน (daily_rate = NULL) กดบันทึกแล้วได้
 *  "daily_rate: Expected number, received string" — แก้อะไรในโปรไฟล์ไม่ได้เลย */
const blankOptional = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((v) => (v === '' ? undefined : v), schema);

// === Auth ===
export const meRequestSchema = z.object({
  idToken: z.string().min(10),
});
export type MeRequest = z.infer<typeof meRequestSchema>;

export const pairRequestSchema = z.object({
  idToken: z.string().min(10),
  // Public tenant code (e.g. STHKJU) — disambiguates pair codes across the
  // shared LINE channel. Auto-generated codes are 6 chars, but Owners can pick
  // a vanity code of 4–12 (ดู TENANT_CODE_PATTERN / signupOwnerSchema) — so this
  // MUST accept the same range, not a hard .length(6), or those tenants' staff
  // can't pair ("validation failed" at the final step). resolved by DB lookup.
  tenant_code: z.string().regex(/^[A-Z0-9]{4,12}$/i, 'รหัสบริษัทไม่ถูกต้อง'),
  code: z.string().length(6),
  // Identity proof captured during pairing so we can attribute future
  // actions to the right person even if the LINE userId changes.
  selfie_path: z.string().min(1),
  national_id_path: z.string().optional(),
});
export type PairRequest = z.infer<typeof pairRequestSchema>;

/** Allowed tenant_code character set + length range (used by both
 *  client-side suggest UI and server-side enforcement). */
export const TENANT_CODE_PATTERN = /^[A-Z0-9]{4,12}$/;

/** Codes the user can't claim — admin / billing / brand-protection words. */
export const TENANT_CODE_RESERVED = new Set([
  'ADMIN', 'ROOT', 'OWNER', 'SYSTEM', 'PAYROLL', 'SUPPORT', 'HELP',
  'TEST', 'DEMO', 'EXAMPLE', 'NULL', 'UNDEFINED', 'STAFF', 'HR',
  'BOSS', 'BILLING', 'INVOICE', 'API', 'STAGING', 'PROD',
  'PAYROLLEDGE', 'EDGE', 'MOODATA',
]);

export const signupOwnerSchema = z.object({
  // สมัครด้วยอีเมล+รหัสผ่าน (ไม่ผ่าน LINE) — LINE เป็นการผูกทีหลังแบบเลือกได้
  email: z.string().email('รูปแบบอีเมลไม่ถูกต้อง').max(254),
  password: z.string().min(8, 'รหัสผ่านอย่างน้อย 8 ตัวอักษร').max(200),
  company_name: z.string().min(2).max(100),
  owner_name: z.string().min(2).max(100),
  phone: z.string().max(20).optional(),
  /** Optional vanity code. If omitted, server auto-generates. */
  tenant_code: z
    .string()
    .regex(TENANT_CODE_PATTERN, 'รหัสต้องเป็น A-Z / 0-9 ความยาว 4-12 ตัว')
    .optional(),
});
export type SignupOwnerRequest = z.infer<typeof signupOwnerSchema>;

// === Web login (email + password, no LINE) ===
//
// แชร์ระหว่าง paypilot/jobpilot/evalpilot (อาศัย web_identities table ใน
// Supabase ตัวเดียวกัน). ปกติเซ็ตรหัสครั้งแรกจาก LIFF (Owner → ตั้งค่า
// → เปิด login เว็บ) แล้วใช้ /login เข้าจาก desktop ได้.
export const webLoginSchema = z.object({
  email: z.string().email('รูปแบบอีเมลไม่ถูกต้อง').max(254),
  password: z.string().min(8, 'รหัสผ่านอย่างน้อย 8 ตัวอักษร').max(200),
});
export type WebLoginRequest = z.infer<typeof webLoginSchema>;

export const webSetPasswordSchema = z.object({
  email: z.string().email('รูปแบบอีเมลไม่ถูกต้อง').max(254),
  password: z.string().min(8, 'รหัสผ่านอย่างน้อย 8 ตัวอักษร').max(200),
});
export type WebSetPasswordRequest = z.infer<typeof webSetPasswordSchema>;

// === Leave ===
export const LEAVE_PERIODS = ['full', 'morning', 'afternoon', 'custom'] as const;
export type LeavePeriod = (typeof LEAVE_PERIODS)[number];

export const submitLeaveSchema = z
  .object({
    leave_type: z.enum(LEAVE_TYPES as readonly [string, ...string[]]),
    dates: z.array(dateString).min(1).max(31),
    reason: z.string().max(500).optional(),
    evidence_path: z.string().optional(),
    is_emergency: z.boolean().optional(),
    period: z.enum(LEAVE_PERIODS).optional().default('full'),
    start_time: timeString.optional(),
    end_time: timeString.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.period === 'custom') {
      if (!v.start_time || !v.end_time) {
        ctx.addIssue({
          code: 'custom',
          message: 'custom period requires start_time + end_time',
          path: ['period'],
        });
      } else if (v.end_time <= v.start_time) {
        ctx.addIssue({
          code: 'custom',
          message: 'end_time must be after start_time',
          path: ['end_time'],
        });
      }
    }
    // half-days only make sense on single-day leaves
    if ((v.period === 'morning' || v.period === 'afternoon' || v.period === 'custom') && v.dates.length > 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'half-day / custom period only allowed on single-day leaves',
        path: ['period'],
      });
    }
    // Personal leave (ลากิจ) requires a substantial reason — approvers
    // can otherwise send it back as info_requested anyway.
    if (v.leave_type === 'personal') {
      const r = (v.reason ?? '').trim();
      if (r.length < 10) {
        ctx.addIssue({
          code: 'custom',
          message: 'ลากิจต้องระบุเหตุผลและความจำเป็น (อย่างน้อย 10 ตัวอักษร)',
          path: ['reason'],
        });
      }
    }
  });
export type SubmitLeaveRequest = z.infer<typeof submitLeaveSchema>;

/** Fraction of a working day this period consumes from leave_quota. */
export function leavePeriodDays(period: LeavePeriod, start_time?: string, end_time?: string): number {
  if (period === 'full') return 1;
  if (period === 'morning' || period === 'afternoon') return 0.5;
  if (period === 'custom' && start_time && end_time) {
    const [sh, sm] = start_time.split(':').map(Number);
    const [eh, em] = end_time.split(':').map(Number);
    const minutes = (eh! * 60 + em!) - (sh! * 60 + sm!);
    return Math.max(0, Math.round((minutes / 480) * 100) / 100); // / 8h, 2-dp
  }
  return 1;
}

export const EMERGENCY_NOTICE = {
  sick_ms: 60 * 60 * 1000,
  personal_ms: 3 * 24 * 60 * 60 * 1000,
} as const;

export function isEmergencyLeave(leaveType: string, firstDate: string, now = new Date()): boolean {
  const start = new Date(`${firstDate}T00:00:00+07:00`).getTime();
  const advance = start - now.getTime();
  if (leaveType === 'sick') return advance < EMERGENCY_NOTICE.sick_ms;
  if (leaveType === 'personal') return advance < EMERGENCY_NOTICE.personal_ms;
  return false;
}

// เลขบัตรประชาชน — รับช่องว่าง/ขีดกลาง/สัญลักษณ์ที่ช่องกรอกได้ (UX) แต่ preprocess
// ตัดเหลือ "ตัวเลขล้วน" ก่อน validate 13 หลัก (ว่างได้) → ผลหลัง parse สะอาดเสมอ
// ทำให้ทุก endpoint ที่ใช้ field นี้ยืนยันผ่านแม้ผู้ใช้พิมพ์ 1-2345-67890-12-3
// ค่ามาส์ก (มี •) ปล่อยผ่านไม่ตัด — handler ฝั่ง HR จะ drop ทิ้งเอง (กันเขียนทับค่าจริง)
export const nationalIdField = z.preprocess(
  (v) => (typeof v === 'string' && !v.includes('•') ? v.replace(/\D/g, '') : v),
  z
    .string()
    .refine((s) => s === '' || s.includes('•') || /^\d{13}$/.test(s), 'เลขบัตรประชาชนต้อง 13 หลัก')
    .optional(),
);

// === Self-profile (employee fills the 10 fields tracked for completion %) ===
export const updateProfileSchema = z.object({
  // Identity
  nickname: z.string().max(50).optional().or(z.literal('')),
  national_id: nationalIdField,
  birth_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'รูปแบบวันที่: YYYY-MM-DD')
    .optional()
    .or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  sso_number: z.string().max(20).optional().or(z.literal('')),
  bank: z.string().max(50).optional().or(z.literal('')),
  bank_account_masked: z.string().max(30).optional().or(z.literal('')),
  // Documents — R2 paths from /upload (purpose=identity|evidence)
  id_copy_path: z.string().max(500).optional().or(z.literal('')),
  bankbook_path: z.string().max(500).optional().or(z.literal('')),
  // Address
  address_current: z.string().max(500).optional().or(z.literal('')),
  address_registered: z.string().max(500).optional().or(z.literal('')),
  // Emergency contact
  emergency_name: z.string().max(100).optional().or(z.literal('')),
  emergency_relationship: z.string().max(50).optional().or(z.literal('')),
  emergency_phone: z.string().max(20).optional().or(z.literal('')),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileSchema>;

// === Education + Work-history (N-row sub-resources, optional, NOT in completion %) ===
export const upsertEducationSchema = z.object({
  id: z.string().uuid().optional(),     // omitted = create
  degree: z.string().min(1).max(50),
  institution: z.string().min(1).max(200),
  major: z.string().max(200).optional().or(z.literal('')),
  year_start: z.number().int().min(1900).max(2100).optional().nullable(),
  year_end: z.number().int().min(1900).max(2100).optional().nullable(),
  gpa: z.number().min(0).max(4.5).optional().nullable(),
  note: z.string().max(500).optional().or(z.literal('')),
});
export type UpsertEducationRequest = z.infer<typeof upsertEducationSchema>;

export const upsertWorkHistorySchema = z.object({
  id: z.string().uuid().optional(),
  company: z.string().min(1).max(200),
  position: z.string().min(1).max(200),
  start_date: dateString.optional().or(z.literal('')).nullable(),
  end_date: dateString.optional().or(z.literal('')).nullable(),
  reason_for_leaving: z.string().max(500).optional().or(z.literal('')),
  note: z.string().max(500).optional().or(z.literal('')),
});
export type UpsertWorkHistoryRequest = z.infer<typeof upsertWorkHistorySchema>;

// ประเภทเอกสารพนักงาน (DB ไม่มี CHECK แล้ว — validate ที่ zod ชั้นนี้; เพิ่มชนิดใหม่
// = แก้ที่นี่พอ ไม่ต้อง migration. ดู mig 20260708000003 ที่ drop CHECK).
export const EMPLOYEE_DOC_TYPES = [
  'national_id',
  'house_registration',
  'transcript',
  'medical_certificate',
  'employment_contract',
  'contract_addendum',
  'warning_letter',
  'termination_letter',
  'bank_book',
  'education',
  'other',
] as const;
export type EmployeeDocType = (typeof EMPLOYEE_DOC_TYPES)[number];

// ป้ายไทยของแต่ละประเภท (ใช้ทั้ง frontend + ตอน gen/แจ้งเตือน)
export const EMPLOYEE_DOC_TYPE_LABELS: Record<EmployeeDocType, string> = {
  national_id: 'บัตรประชาชน',
  house_registration: 'สำเนาทะเบียนบ้าน',
  transcript: 'สำเนา Transcript / ผลการเรียน',
  medical_certificate: 'ใบตรวจร่างกาย',
  employment_contract: 'สัญญาจ้าง',
  contract_addendum: 'สัญญาแนบท้าย',
  warning_letter: 'หนังสือเตือน',
  termination_letter: 'หนังสือเลิกจ้าง',
  bank_book: 'สมุดบัญชีธนาคาร',
  education: 'วุฒิการศึกษา',
  other: 'อื่นๆ',
};

// ช่องเอกสารประจำตัวพนักงาน (แสดงเป็นรายการตามชื่อในหน้าแก้ไขพนักงาน แท็บเอกสาร).
// กลุ่มบังคับ (REQUIRED_EMPLOYEE_DOC_TYPES) ขึ้นก่อน แล้วตามด้วยที่แนบถ้ามี.
export const PERSONNEL_DOC_SLOTS: EmployeeDocType[] = [
  'national_id',
  'house_registration',
  'bank_book',
  'transcript',
  'medical_certificate',
  'employment_contract',
  'contract_addendum',
];

// เอกสาร "ส่วนตัวของพนักงาน" ที่พนักงานต้องเป็นคนส่งเอง — ใช้หน้าติดตามเอกสารที่ขาด + ปุ่มทวง.
// ⚠️ ห้ามใส่เอกสาร "ของบริษัท" (สัญญาจ้าง/หนังสือเตือน/เลิกจ้าง) — บริษัทเป็นคนออก/จัดการ
// ไม่ต้องทวงพนักงาน (สัญญาจ้างมี flow แยก = สร้าง+ให้เซ็น ผ่าน pending_sign).
export const REQUIRED_EMPLOYEE_DOC_TYPES: EmployeeDocType[] = [
  'national_id',
  'house_registration',
  'bank_book',
];
// เอกสารของบริษัท (จัดการฝั่งบริษัท ไม่ทวงพนักงาน) — ไว้จัดกลุ่ม/อ้างอิงใน UI
export const COMPANY_EMPLOYEE_DOC_TYPES: EmployeeDocType[] = [
  'employment_contract',
  'warning_letter',
  'termination_letter',
];

const employeeDocTypeEnum = z.enum(EMPLOYEE_DOC_TYPES);

export const createEmployeeDocumentSchema = z.object({
  label: z.string().min(1).max(100),
  r2_path: z.string().min(1).max(500),
  mime_type: z.string().max(100).optional(),
  size_bytes: z.number().int().nonnegative().optional(),
  note: z.string().max(500).optional().or(z.literal('')),
  doc_type: employeeDocTypeEnum.optional(),
});
export type CreateEmployeeDocumentRequest = z.infer<typeof createEmployeeDocumentSchema>;

// gen สัญญาจ้างจากข้อมูลในระบบ → สร้างเอกสารสถานะ 'awaiting' + แจ้งพนักงานเซ็น
export const generateContractSchema = z.object({
  emp_code: z.string().min(1),
  effective_date: dateString.optional().or(z.literal('')),
  probation_months: z.number().int().min(0).max(4).optional(),
  // 4,000 = ยาวพอใส่ข้อตกลงพิเศษจากคลังเอกสารทั้งฉบับ (เดิม 500)
  note: z.string().max(4000).optional().or(z.literal('')),
  notify: z.boolean().optional(), // ส่ง LINE ให้พนักงานเซ็นเลยไหม (default true)
  // คลังเอกสารสัญญา: ไม่ระบุ = ฉบับค่าเริ่มต้นของบริษัท
  template_id: z.string().uuid().optional(),
  // สัญญามีกำหนดเวลา — ไม่ระบุ = ไม่มีกำหนด
  end_date: dateString.optional().or(z.literal('')),
});
export type GenerateContractRequest = z.infer<typeof generateContractSchema>;

// พนักงานเซ็นเอกสาร: onscreen=วาดลายเซ็น (signature_r2_path) / upload=อัปรูปเอกสารเซ็นแล้ว (signed_r2_path)
export const signDocumentSchema = z
  .object({
    method: z.enum(['onscreen', 'upload']),
    signature_r2_path: z.string().max(500).optional(),
    signed_r2_path: z.string().max(500).optional(),
  })
  .refine(
    (v) => (v.method === 'onscreen' ? !!v.signature_r2_path : !!v.signed_r2_path),
    { message: 'ต้องแนบไฟล์ลายเซ็น/เอกสารที่เซ็นแล้ว' },
  );
export type SignDocumentRequest = z.infer<typeof signDocumentSchema>;

// HR ทวงเอกสารที่ขาด → ส่ง LINE. เว้น emp_codes = ทุกคนที่ขาดเอกสารตาม doc_types
export const remindDocumentsSchema = z.object({
  emp_codes: z.array(z.string()).optional(),
  doc_types: z.array(employeeDocTypeEnum).optional(),
});
export type RemindDocumentsRequest = z.infer<typeof remindDocumentsSchema>;

// === Onboarding (เช็คลิสต์รับพนักงานใหม่ ตั้งค่าได้) ===
export const ONBOARDING_CATEGORIES = ['document', 'setting', 'signature'] as const;
export type OnboardingCategory = (typeof ONBOARDING_CATEGORIES)[number];
export const ONBOARDING_CATEGORY_LABELS: Record<OnboardingCategory, string> = {
  document: 'เอกสารที่ต้องส่ง',
  setting: 'ตั้งค่าที่ต้องทำ',
  signature: 'เอกสารที่ต้องเซ็น',
};

// auto_check rule ids ที่ backend ตรวจให้อัตโนมัติ (null = ติ๊กมือ). doc rule ใช้รูป
// `document:<doc_type>`. รายการนี้ให้ frontend โชว์ตัวเลือกตอนตั้งค่าเทมเพลต.
export const ONBOARDING_AUTO_RULES = [
  'salary_set',        // ตั้งฐานเงินเดือนแล้ว (salary_master base>0)
  'department_set',    // มีแผนก + ตำแหน่ง
  'line_paired',       // พนักงานผูก LINE แล้ว (users.line_user_id ไม่ใช่ web:)
  'contract_signed',   // เซ็นสัญญาจ้างแล้ว (employee_documents employment_contract signed)
] as const;

export interface OnboardingItem {
  item_key: string;
  category: OnboardingCategory;
  label: string;
  doc_type?: EmployeeDocType | null;
  auto_check?: string | null;
  required: boolean;
  sort_order: number;
  active?: boolean;
}

// เทมเพลตเริ่มต้น (worker lazy-seed ให้ tenant ที่ยังไม่เคยตั้ง) — HR แก้ได้ทีหลัง
export const DEFAULT_ONBOARDING_ITEMS: OnboardingItem[] = [
  { item_key: 'doc_national_id', category: 'document', label: 'ส่งสำเนาบัตรประชาชน', doc_type: 'national_id', auto_check: 'document:national_id', required: true, sort_order: 10 },
  { item_key: 'doc_house_registration', category: 'document', label: 'ส่งสำเนาทะเบียนบ้าน', doc_type: 'house_registration', auto_check: 'document:house_registration', required: true, sort_order: 20 },
  { item_key: 'doc_bank_book', category: 'document', label: 'ส่งสำเนาสมุดบัญชีธนาคาร', doc_type: 'bank_book', auto_check: 'document:bank_book', required: true, sort_order: 30 },
  { item_key: 'doc_education', category: 'document', label: 'ส่งวุฒิการศึกษา', doc_type: 'education', auto_check: 'document:education', required: false, sort_order: 40 },
  { item_key: 'set_salary', category: 'setting', label: 'ตั้งฐานเงินเดือน', auto_check: 'salary_set', required: true, sort_order: 50 },
  { item_key: 'set_department', category: 'setting', label: 'กำหนดแผนก / ตำแหน่ง', auto_check: 'department_set', required: true, sort_order: 60 },
  { item_key: 'set_line', category: 'setting', label: 'พนักงานผูกบัญชี LINE', auto_check: 'line_paired', required: true, sort_order: 70 },
  { item_key: 'orientation', category: 'setting', label: 'ปฐมนิเทศ / แนะนำกฎระเบียบบริษัท', auto_check: null, required: false, sort_order: 80 },
  { item_key: 'sign_contract', category: 'signature', label: 'เซ็นสัญญาจ้าง', doc_type: 'employment_contract', auto_check: 'contract_signed', required: true, sort_order: 90 },
];

const onboardingItemInputSchema = z.object({
  item_key: z.string().min(1).max(60),
  category: z.enum(ONBOARDING_CATEGORIES),
  label: z.string().min(1).max(120),
  doc_type: employeeDocTypeEnum.nullable().optional(),
  auto_check: z.string().max(60).nullable().optional(),
  required: z.boolean(),
  sort_order: z.number().int(),
  active: z.boolean().optional(),
});

// บันทึกเทมเพลตทั้งชุด (replace) — HR เพิ่ม/ลบ/แก้/จัดลำดับ
export const saveOnboardingTemplateSchema = z.object({
  items: z.array(onboardingItemInputSchema).max(50),
});
export type SaveOnboardingTemplateRequest = z.infer<typeof saveOnboardingTemplateSchema>;

// ติ๊ก/ยกเลิกติ๊กรายการมือ (manual item) รายคน
export const markOnboardingItemSchema = z.object({
  item_key: z.string().min(1),
  done: z.boolean(),
  note: z.string().max(500).optional().or(z.literal('')),
});
export type MarkOnboardingItemRequest = z.infer<typeof markOnboardingItemSchema>;

// เริ่ม/จบ onboarding รายคน
export const setOnboardingStatusSchema = z.object({
  status: z.enum(['active', 'done']).nullable(),
});
export type SetOnboardingStatusRequest = z.infer<typeof setOnboardingStatusSchema>;

export const respondInfoRequestSchema = z.object({
  leave_id: z.string(),
  response_text: z.string().min(1).max(1000),
  evidence_path: z.string().optional(),
});

export const submitConditionalEvidenceSchema = z.object({
  leave_id: z.string(),
  evidence_path: z.string().min(1),
});

// === OT ===
export const submitOTSchema = z.object({
  date: dateString,
  start_time: timeString,
  end_time: timeString,
  // ผู้ใช้ไม่ต้องเลือกประเภท — server derive จากวันที่เสมอ (กันคิดผิด). optional ไว้
  // เผื่อ client เก่ายังส่งมา แต่ค่าจะถูกเพิกเฉย.
  ot_type: z.enum(OT_TYPES as readonly [string, ...string[]]).optional(),
  reason: z.string().min(1).max(500),
});
export type SubmitOTRequest = z.infer<typeof submitOTSchema>;

// === ยกเลิกคำขอของตัวเอง (ลา/OT ที่ยังรออนุมัติ) ===
export const cancelRequestSchema = z.object({
  kind: z.enum(['leave', 'ot']),
  record_id: z.string().min(1), // leave_records.id / ot_requests.id (uuid)
});
export type CancelRequest = z.infer<typeof cancelRequestSchema>;

// === Owner/HR ขอยกเลิกใบที่ "อนุมัติแล้ว แต่ยังมาไม่ถึง" (ต้องให้พนักงานยินยอม) ===
export const requestCancelApprovedSchema = z.object({
  kind: z.enum(['leave', 'ot']),
  record_id: z.string().min(1),
  reason: z.string().min(1, 'ต้องระบุเหตุผล').max(500),
});
export type RequestCancelApproved = z.infer<typeof requestCancelApprovedSchema>;

// === พนักงานตอบคำขอยกเลิกของ owner/HR (ยินยอม / ไม่ยินยอม) ===
export const consentCancelSchema = z.object({
  kind: z.enum(['leave', 'ot']),
  record_id: z.string().min(1),
  consent: z.boolean(), // true = ยินยอมให้ยกเลิก, false = ไม่ยินยอม (คงใบไว้)
});
export type ConsentCancel = z.infer<typeof consentCancelSchema>;

// === Check-in ===
export const CHECKIN_KINDS = ['morning_in', 'lunch_out', 'lunch_in', 'evening_out'] as const;
export type CheckinKind = (typeof CHECKIN_KINDS)[number];

export const submitCheckinSchema = z.object({
  selfie_path: z.string().min(1), // R2 path from POST /upload
  // ⭐ nullable = "ไม่มีพิกัด" (ไม่ให้สิทธิ์ / จับสัญญาณไม่ได้) ซึ่งต่างจาก
  // "อยู่ไกลสาขา" คนละเรื่องกัน. bundle เก่าที่ค้างในเครื่องผู้ใช้ยังส่ง 0,0 มา
  // → handler แปลง 0,0 เป็น "ไม่มีพิกัด" ให้อีกชั้น (ดู hasGpsFix ใน checkin.ts)
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  kind: z.enum(CHECKIN_KINDS),
  // ความแม่นยำ GPS (เมตร) จาก device — เก็บไว้ถ่วงน้ำหนักตอนเรียนรู้พิกัดออฟฟิศ
  accuracy: z.number().nonnegative().optional(),
  // เฉพาะพนักงาน hybrid ที่ต้องเลือกโหมดต่อวัน — office/wfh คงที่ไม่ต้องส่งมา (handler ใช้ work_mode)
  location_mode: z.enum(['office', 'wfh']).optional(),
});
export type SubmitCheckinRequest = z.infer<typeof submitCheckinSchema>;

// === Work mode (geofence learning + WFH) ===
export const WORK_MODES = ['office', 'wfh', 'hybrid'] as const;
export type WorkMode = (typeof WORK_MODES)[number];

// เพศ — ใช้ในทะเบียนพนักงาน/รายงานราชการ. เก็บ null ได้ (ยังไม่กรอก).
export const GENDERS = ['male', 'female', 'other'] as const;
export type Gender = (typeof GENDERS)[number];
export const GENDER_LABELS: Record<Gender, string> = {
  male: 'ชาย',
  female: 'หญิง',
  other: 'อื่น ๆ',
};

// === Upload presign ===
export const FILE_PURPOSES = ['selfie', 'evidence', 'avatar', 'identity', 'temp'] as const;
export type FilePurposeShared = (typeof FILE_PURPOSES)[number];

export const presignUploadSchema = z.object({
  ext: z.enum(['jpg', 'jpeg', 'png', 'pdf']),
  purpose: z.enum(['selfie', 'evidence', 'avatar', 'identity']),
});
export type PresignUploadRequest = z.infer<typeof presignUploadSchema>;

// === Approval action ===
export const approvalActionSchema = z.object({
  kind: z.enum(['leave', 'ot']),
  id: z.string(),                        // leave_id or ot_id
  action: z.enum(['approve', 'reject', 'request_info', 'conditional_approve']),
  reason: z.string().max(500).optional(),
  info_message: z.string().max(500).optional(),
  evidence_due_at: z.string().optional(),  // ISO datetime for conditional
});
export type ApprovalActionRequest = z.infer<typeof approvalActionSchema>;

// === HR CRUD ===
export const upsertEmployeeSchema = z.object({
  emp_code: empCodeString,
  // ไม่มีช่องบังคับในหน้าข้อมูลพนักงาน — ชื่อ/นามสกุลกรอกทีหลังได้
  // (handler จะ default เป็น '' กัน NOT NULL ของคอลัมน์ใน DB)
  first_name: z.string().optional().or(z.literal('')),
  last_name: z.string().optional().or(z.literal('')),
  nickname: z.string().optional().or(z.literal('')),
  // email/department/position/start_date are optional — a minimal add fills
  // only name + national_id; the rest comes later (employee via LINE / HR).
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  department: z.string().optional().or(z.literal('')),
  position: z.string().optional().or(z.literal('')),
  supervisor_email: z.string().email().optional().or(z.literal('')),
  // หัวหน้างาน = เลือกชื่อพนักงาน (emp_code). '' = ไม่มีหัวหน้า
  supervisor_emp_code: z.string().optional().or(z.literal('')),
  start_date: dateString.optional().or(z.literal('')),
  end_date: dateString.optional().or(z.literal('')),
  status: z.enum(['active', 'probation', 'pending_start', 'resigned', 'terminated']),
  // แหล่งตัดสินวันหยุดตอนคิดเงิน (วันหยุดหมุนเวียน) — ดู SHIFT_ROSTER_BLUEPRINT.md
  //   company_weekend = ใช้วันหยุดบริษัท (weekend_days) · roster = ยึดตารางเวรรายคน
  schedule_mode: blankOptional(z.enum(['company_weekend', 'roster']).optional()),
  // ไม่ต้องสแกนเข้างาน/ไม่นับเวลา → จ่ายเต็ม (เจาะรายคน เช่น ผู้บริหาร/เหมาเดือน)
  attendance_exempt: blankOptional(z.boolean().optional()),
  // รูปแบบการทำงาน — office (geofence คำนวณเสมอ) / wfh (ข้าม geofence) / hybrid (เลือกต่อวัน)
  work_mode: blankOptional(z.enum(WORK_MODES).optional()),
  // สาขาหลักที่สังกัด (สุกรีเภสัช หลายสาขา) — ใช้จัดกลุ่มเงินเดือน/รายงาน. ส่ง '' = ปลดออก
  branch_id: z.string().uuid().nullable().optional().or(z.literal('')),
  // เข้าประกันสังคมหรือไม่ (true=หัก ปกส. 5% ตอนคิดเงินเดือน) — เจาะรายคน
  sso_eligible: blankOptional(z.boolean().optional()),
  // หักภาษี ณ ที่จ่ายหรือไม่ (true=หักอัตโนมัติ) — เจาะรายคน (payroll.ts อ่าน tax_withhold)
  tax_withhold: blankOptional(z.boolean().optional()),
  // มีค่าคอมมิชชั่น — งวดเงินเดือนจ่ายแค่ฐาน+ภาษี/ปกส./กองทุน, รายการหัก/OT/เงินเพิ่ม-หัก
  // ย้ายไปหักรอบจ่ายค่าคอม (default false = คิดตามเดิม)
  has_commission: blankOptional(z.boolean().optional()),
  // ประเภทการจ้าง + เรทรายวัน (mig 20260523000001) — เดิมไม่มี endpoint ไหนเขียนได้เลย
  // (เพิ่ม 2026-07-20 เพื่อ bulk-import สุกรีเภสัช: รายวัน 347/วัน, PT เภสัชแปลงเป็นรายวัน)
  employee_type: blankOptional(z.enum(['monthly', 'daily']).optional()),
  daily_rate: blankOptional(z.number().nonnegative().nullable().optional()),
  // รายวันจ่ายเฉพาะวันที่มา (+วันหยุดประเพณี+ลาป่วย ตาม §56/§57 — ดู dailyPaidDays)
  pay_only_attended: blankOptional(z.boolean().optional()),
  // แพทเทิร์นหมุนเวรอัตโนมัติ — ผูก pattern + วันเริ่มรอบ (ส่ง '' = ปลดออก)
  rotation_pattern_id: z.string().uuid().nullable().optional().or(z.literal('')),
  rotation_anchor_date: dateString.nullable().optional().or(z.literal('')),
  // Profile fields used by compute_profile_completion_pct trigger
  national_id: nationalIdField,
  birth_date: dateString.optional().or(z.literal('')),
  sso_number: z.string().optional().or(z.literal('')),
  bank: z.string().optional().or(z.literal('')),
  bank_account_masked: z.string().optional().or(z.literal('')),
  // Full account number for the bank-transfer file. Stored encrypted
  // (bank_account_encrypted); never persisted as plaintext. Handler derives
  // bank_account_masked (last 4) from it. Send '' to leave unchanged.
  bank_account_full: z.string().max(30).regex(/^[0-9-]*$/, 'เลขบัญชีตัวเลขเท่านั้น').optional().or(z.literal('')),
  // Extended profile (added 2026-05-21)
  id_copy_path: z.string().max(500).optional().or(z.literal('')),
  bankbook_path: z.string().max(500).optional().or(z.literal('')),
  address_current: z.string().max(500).optional().or(z.literal('')),
  address_registered: z.string().max(500).optional().or(z.literal('')),
  emergency_name: z.string().max(100).optional().or(z.literal('')),
  emergency_relationship: z.string().max(50).optional().or(z.literal('')),
  emergency_phone: z.string().max(20).optional().or(z.literal('')),
  // === ทะเบียนพนักงานฉบับเต็ม (mig 20260819000000) — ช่องที่รายงาน 42 คอลัมน์ต้องใช้ ===
  // ชื่อ/คำนำหน้า TH+EN (ระบบเดิมเก็บแค่ first_name/last_name ไทย)
  title_th: z.string().max(30).optional().or(z.literal('')),
  title_en: z.string().max(30).optional().or(z.literal('')),
  first_name_en: z.string().max(100).optional().or(z.literal('')),
  last_name_en: z.string().max(100).optional().or(z.literal('')),
  nickname_en: z.string().max(50).optional().or(z.literal('')),
  gender: blankOptional(z.enum(GENDERS).nullable().optional()),
  nationality: z.string().max(50).optional().or(z.literal('')),
  // โครงสร้างองค์กรชั้นบน/ล่างของแผนก + ระดับตำแหน่ง (ข้อความอิสระเหมือน department/position)
  division: z.string().max(100).optional().or(z.literal('')),
  section: z.string().max(100).optional().or(z.literal('')),
  employee_group: z.string().max(100).optional().or(z.literal('')),
  position_level: blankOptional(z.number().int().min(0).max(99).nullable().optional()),
  // รหัสในเครื่องสแกนลายนิ้วมือ (ใช้จับคู่ตอน import ไฟล์เวลาจากเครื่อง)
  fingerprint_id: z.string().max(50).optional().or(z.literal('')),
  // วันที่บรรจุ (ผ่านทดลองงาน) / วันหมดสัญญาจ้าง — คนละตัวกับ start_date/end_date
  appointed_date: dateString.optional().or(z.literal('')),
  contract_end_date: dateString.optional().or(z.literal('')),
  // เอกสารต่างด้าว (ว่างสำหรับคนไทย)
  alien_id: z.string().max(20).optional().or(z.literal('')),
  passport_no: z.string().max(30).optional().or(z.literal('')),
  work_permit_no: z.string().max(30).optional().or(z.literal('')),
  // ห้ามจ้างซ้ำ — โชว์ในทะเบียน ไม่ได้บล็อกอะไรอัตโนมัติ
  blacklisted: blankOptional(z.boolean().optional()),
  blacklist_note: z.string().max(500).optional().or(z.literal('')),
  // ค่าจ้างรายสัปดาห์ (ยังไม่มีเอนจินจ่ายรายสัปดาห์ — เก็บไว้ในทะเบียน/รายงานเท่านั้น)
  weekly_wage: blankOptional(z.number().nonnegative().nullable().optional()),
});

// Bulk employee master import — array of full employee rows (no salary).
// Each row reuses upsertEmployeeSchema, so the same field rules apply
// (emp_code required, status enum, national_id optional, bank_account_full
// numeric). Capped to keep one request under the Workers subrequest limit.
export const bulkImportEmployeesSchema = z.object({
  employees: z.array(upsertEmployeeSchema).min(1).max(500),
});

export const upsertAllowanceSchema = z.object({
  emp_code: empCodeString,
  category: z.string().min(1),
  amount: z.number().nonnegative(),
  effective_from: dateString,
  effective_to: dateString.optional(),
  note: z.string().optional(),
});

export const upsertHolidaySchema = z.object({
  date: dateString,
  name: z.string().min(1),
  type: z.enum(['public', 'company', 'substitute']),
  applies_to: z.string().default('all'),
});

export const upsertLeaveQuotaSchema = z.object({
  emp_code: empCodeString,
  year: z.number().int().min(2020).max(2100),
  sick_quota: z.number().nonnegative(),
  personal_quota: z.number().nonnegative(),
  vacation_quota: z.number().nonnegative(),
  carried_days: z.number().nonnegative().optional(),
  opening_sick: z.number().nonnegative().optional(),
  opening_personal: z.number().nonnegative().optional(),
  opening_vacation: z.number().nonnegative().optional(),
});

// === Leave packages (2026-05-23) ===
export const upsertLeavePackageSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1).max(80),
  description: z.string().max(280).optional().nullable(),
  sick_quota: z.number().nonnegative(),
  personal_quota: z.number().nonnegative(),
  vacation_quota: z.number().nonnegative(),
  prorate_on_join: z.boolean().default(true),
  carry_over_max: z.number().nonnegative().default(0),
  /** กฎ §30 ม.แรงงาน — false (default) = ปีแรกไม่ได้สิทธิ์พักร้อน, true = ได้ตั้งแต่วันแรก */
  vacation_first_year_eligible: z.boolean().default(false),
});

export const assignLeavePackageSchema = z.object({
  package_id: z.string().uuid(),
  year: z.number().int().min(2020).max(2100),
  emp_codes: z.array(empCodeString).min(1).max(500),
  /** If true (default), prorate quotas based on employees.start_date for the
   *  given year. False = full quota regardless of join date. */
  apply_prorate: z.boolean().default(true),
});

export const rolloverLeaveSchema = z.object({
  /** Year that's ending. Carry-over flows from {year} → {year+1}. */
  from_year: z.number().int().min(2020).max(2100),
});

// === Owner / Payroll ===
export const runReconcileSchema = z.object({
  period: periodString,
  /** 'final' = period_end already passed; 'preview' = mid-period peek
   *  with projection warning for un-elapsed days. */
  mode: z.enum(['final', 'preview']).optional().default('final'),
});

export const runPayrollSchema = z.object({
  period: periodString,
  force: z.boolean().default(false),
  /** Optional trigger reason recorded on the payroll_revisions row. */
  trigger_reason: z.string().max(50).optional(),
  /**
   * โหมดเงินเดือนล้วน (per-run override). undefined = ใช้ค่าบริษัท
   * (tenant_settings.salary_only). true = งวดนี้ข้ามการลงเวลา/ลา/OT จ่ายเต็ม
   * (ฐาน+เพิ่ม+หัก+ภาษี/ปกส.). false = บังคับคิดตามการลงเวลาแม้บริษัทตั้ง salary_only.
   */
  salary_only: z.boolean().optional(),
});

export const payrollPeriodActionSchema = z.object({
  period: periodString,
});

export const payrollRejectSchema = z.object({
  period: periodString,
  reason: z.string().min(1).max(500),
});

// === เบี้ยขยันขั้นบันได (diligence ladder) ===
export const DILIGENCE_LEAVE_TYPES = ['sick', 'personal', 'vacation', 'unpaid', 'maternity'] as const;
export const diligenceLadderSchema = z.object({
  active: z.boolean().default(true),
  name: z.string().max(100).optional(),
  // จำนวนเงินแต่ละขั้น (index 0 = ขั้น 1) — ต้องเรียงและไม่ติดลบ
  steps: z.array(z.number().nonnegative()).max(20).default([]),
  start_tier: z.number().int().min(1).default(1),
  break_on_late: z.boolean().default(true),
  break_on_absent: z.boolean().default(true),
  break_leave_types: z.array(z.enum(DILIGENCE_LEAVE_TYPES)).default([]),
  is_taxable: z.boolean().default(true),
  is_ssoable: z.boolean().default(true),
  is_pfable: z.boolean().default(true),
});
export type DiligenceLadderInput = z.infer<typeof diligenceLadderSchema>;

// === Phase 1B — Allowance master CRUD ===
export const ALLOWANCE_PRESETS = [
  'position', 'cola', 'travel_fixed', 'travel_actual',
  'meal', 'phone_actual', 'commission_base', 'custom',
] as const;
export const ALLOWANCE_FREQUENCIES = ['fixed_monthly', 'variable'] as const;

export const upsertAllowanceMasterSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().min(1).max(40),
  name_th: z.string().min(1).max(100),
  preset_type: z.enum(ALLOWANCE_PRESETS),
  is_taxable: z.boolean().default(true),
  is_ssoable: z.boolean().default(true),
  is_pfable: z.boolean().default(true),
  frequency: z.enum(ALLOWANCE_FREQUENCIES).default('fixed_monthly'),
  default_amount: z.number().nonnegative().nullable().optional(),
  default_rate: z.number().nonnegative().nullable().optional(),
  rate_unit: z.string().max(20).nullable().optional(),
  condition_type: z.enum(['perfect_attendance']).nullable().optional(),
  active: z.boolean().default(true),
});

export const upsertAllowanceEligibilitySchema = z.object({
  id: z.string().uuid().optional(),
  emp_code: z.string().min(1).max(20),
  allowance_id: z.string().uuid(),
  fixed_amount: z.number().nonnegative().nullable().optional(),
  rate: z.number().nonnegative().nullable().optional(),
  start_date: dateString,
  end_date: dateString.optional().or(z.literal('')).nullable(),
  active: z.boolean().default(true),
});

// === Recurring deduction master + eligibility ===
export const DEDUCTION_KINDS = ['statutory', 'fixed'] as const;

export const upsertDeductionMasterSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().min(1).max(40),
  name_th: z.string().min(1).max(100),
  kind: z.enum(DEDUCTION_KINDS).default('fixed'),
  default_amount: z.number().nonnegative().nullable().optional(),
  note: z.string().max(200).nullable().optional(),
  active: z.boolean().default(true),
});

export const upsertDeductionEligibilitySchema = z.object({
  id: z.string().uuid().optional(),
  emp_code: z.string().min(1).max(20),
  deduction_id: z.string().uuid(),
  amount: z.number().nonnegative().nullable().optional(),
  start_date: dateString,
  end_date: dateString.optional().or(z.literal('')).nullable(),
  active: z.boolean().default(true),
});

// === เรื่อง 1 — emergency leave from a missed scan ===
export const requestEmergencyLeaveSchema = z.object({
  date: dateString,
  reason: z.string().max(500).optional().or(z.literal('')),
});

export const decideEmergencyLeaveSchema = z.object({
  leave_id: z.string().min(1),
  /** paid = ลาได้เงิน; unpaid = ลาไม่ได้เงิน; absent = ขาดงาน */
  decision: z.enum(['paid', 'unpaid', 'absent']),
  reason: z.string().min(1).max(500),
  /** HR's call: also deduct from the leave quota? */
  deduct_quota: z.boolean(),
});

// === เรื่อง 3 — self-confirm clock-out ===
export const confirmClockOutSchema = z.object({
  date: dateString,
  /** 'shift_end' = ใช้เวลาเลิกตามกะ (ปลอดภัย ไม่ overpay); 'actual' = ระบุเวลาจริง. */
  mode: z.enum(['shift_end', 'actual']),
  /** required when mode='actual' */
  actual_time: timeString.optional(),
  reason: z.string().max(300).optional().or(z.literal('')),
});

// === Tickets (ร้องเรียน / แจ้งเหตุ / ร้องทุกข์ + คำขอแก้ย้อนหลัง) ===
export const TICKET_CATEGORIES = [
  'complaint', 'report', 'grievance',
  'leave_backdate', 'leave_type_change', 'leave_edit_locked', 'schedule_change',
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];

export const createTicketSchema = z.object({
  category: z.enum(TICKET_CATEGORIES),
  subject: z.string().min(1).max(150),
  detail: z.string().min(1).max(2000),
  ref_note: z.string().max(300).optional().or(z.literal('')),
});

export const triageTicketSchema = z.object({
  id: z.string().uuid(),
  action: z.enum(['review', 'escalate', 'resolve']),
  hr_note: z.string().max(1000).optional().or(z.literal('')),
});

export const decideTicketSchema = z.object({
  id: z.string().uuid(),
  decision: z.enum(['approved', 'rejected']),
  decision_note: z.string().min(1).max(1000),
});

// === Phase 1B — Data entry endpoints ===
export const upsertVariableAllowanceEntrySchema = z.object({
  period: periodString,
  emp_code: z.string().min(1).max(20),
  allowance_id: z.string().uuid(),
  qty: z.number().nonnegative(),
});

/**
 * Adjustment presets — decide how a one-off "เงินเพิ่ม" hits the
 * tax / SSO / PF bases. Tax ≠ SSO: a bonus is taxable income but is NOT
 * "ค่าจ้าง" for SSO (ม.5 พรบ.ปกส.), so it must not enter the SSO base.
 * Presets apply to direction='add' only (income); deductions ignore them.
 */
export const ADJUSTMENT_PRESETS = {
  // โบนัส / เงินรางวัล / เงินเพิ่มพิเศษ — เสียภาษี แต่ไม่เข้า ปกส./PF
  bonus: { is_taxable: true, is_ssoable: false, is_pfable: false },
  // ปรับค่าจ้างตกหล่น (เช่น OT/ค่าจ้างที่ลืมจ่าย) — เข้าทุกฐาน
  wage_correction: { is_taxable: true, is_ssoable: true, is_pfable: true },
  // เบิกคืนตามจริง / ไม่ใช่เงินได้ — ไม่เข้าฐานใดเลย
  reimbursement: { is_taxable: false, is_ssoable: false, is_pfable: false },
} as const;
export type AdjustmentPreset = keyof typeof ADJUSTMENT_PRESETS;

export const upsertAdjustmentEntrySchema = z.object({
  period: periodString,
  emp_code: z.string().min(1).max(20),
  direction: z.enum(['add', 'deduct']),
  label: z.string().min(1).max(100),
  amount: z.number().positive(),
  /** Tax/SSO/PF treatment for an 'add'. Default 'bonus' (taxable, not SSO).
   *  Ignored for 'deduct'. */
  preset: z.enum(['bonus', 'wage_correction', 'reimbursement']).optional(),
  notes: z.string().max(500).optional().or(z.literal('')),
});

/** Bulk import of one-off adjustments (เงินเพิ่ม/หัก หลายคนพร้อมกันจากไฟล์ Excel/CSV).
 *  Each entry follows the same shape as a single adjustment; the handler
 *  upserts them all in one query. Cap 2000 = roughly the largest tenant. */
export const bulkUpsertAdjustmentSchema = z.object({
  period: periodString,
  entries: z
    .array(
      z.object({
        emp_code: z.string().min(1).max(20),
        direction: z.enum(['add', 'deduct']),
        label: z.string().min(1).max(100),
        amount: z.number().positive(),
        preset: z.enum(['bonus', 'wage_correction', 'reimbursement']).optional(),
      }),
    )
    .min(1)
    .max(2000),
});

/** Per-period pay override — "จ่ายฐานเท่านี้เดือนนี้" รายคน. amount=ยอดฐานที่จ่าย
 *  งวดนั้น (แทน basePay); เมื่อคำนวณ engine จะปิดรายการอัตโนมัติทั้งหมดสำหรับคนนี้
 *  (docking/OT/allowance/diligence/commission/loan) แต่ statutory + เพิ่ม/หัก ในไฟล์
 *  ยังคิดตามปกติ. amount=0 = ลบ override (จ่ายเป็น auto). */
export const upsertPayOverrideSchema = z.object({
  period: periodString,
  emp_code: z.string().min(1).max(20),
  amount: z.number().min(0),
  source: z.enum(['manual', 'file']).optional(),
  notes: z.string().max(500).optional().or(z.literal('')),
});

/** Bulk override จากไฟล์ (คอลัมน์ "ยอดจ่ายเดือนนี้") — upsert หลายคนพร้อมกัน. */
export const bulkUpsertPayOverrideSchema = z.object({
  period: periodString,
  entries: z
    .array(z.object({ emp_code: z.string().min(1).max(20), amount: z.number().min(0) }))
    .min(1)
    .max(2000),
});

// === Phase 3 — Shift master ===
export const NIGHT_PREMIUM_TYPES = ['none', 'percent', 'flat'] as const;

export const upsertShiftMasterSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(100),
  start_time: timeString,
  end_time: timeString,
  crosses_midnight: z.boolean().default(false),
  break_minutes: z.number().int().nonnegative().default(60),
  paid_hours: z.number().positive().max(24).default(8),
  night_premium_type: z.enum(NIGHT_PREMIUM_TYPES).default('none'),
  night_premium_value: z.number().nonnegative().default(0),
  night_window_start: timeString.default('22:00'),
  night_window_end: timeString.default('06:00'),
  default_weekdays: z.array(z.number().int().min(1).max(7)).default([1, 2, 3, 4, 5, 6]),
  active: z.boolean().default(true),
  // กะยืดหยุ่น: ไม่คิดสาย · สแกนเข้าอย่างเดียว (ไม่บังคับออก) · ต้องสแกนเข้าภายใน flex_check_in_by
  flexible: z.boolean().default(false),
  require_clock_out: z.boolean().default(true),
  flex_check_in_by: timeString.nullable().optional(),
});

export const upsertShiftAssignmentSchema = z.object({
  id: z.string().uuid().optional(),
  emp_code: z.string().min(1).max(20),
  shift_id: z.string().uuid(),
  weekdays: z.array(z.number().int().min(1).max(7)).min(1),
  start_date: dateString,
  end_date: dateString.optional().or(z.literal('')).nullable(),
  active: z.boolean().default(true),
});

export const upsertShiftRosterSchema = z.object({
  emp_code: z.string().min(1).max(20),
  date: dateString,
  shift_id: z.string().uuid().nullable(),  // null = explicit day-off
  note: z.string().max(200).optional().or(z.literal('')),
});

// === วันหยุดหมุนเวียน / นำเข้าตารางเวร (P2/P3) ===

// บันทึกหลายช่องรวดเดียว (drag-paint ในปฏิทิน). shift_id=null = วันหยุด.
export const upsertShiftRosterBulkSchema = z.object({
  entries: z.array(z.object({
    emp_code: z.string().min(1).max(20),
    date: dateString,
    shift_id: z.string().uuid().nullable(),
    note: z.string().max(200).optional().or(z.literal('')),
  })).min(1).max(3000),
});

// ตารางแปลรหัส (Code Mapping) ระดับ tenant — เก็บใน settings เป็น JSON.
// key = ข้อความดิบ (normalize แล้ว) · value = shift code | '__OFF__' | '__IGNORE__'
export const shiftCodeAliasSchema = z.object({
  aliases: z.record(z.string(), z.string()),
});

// นำเข้าตารางเวรจาก Excel/paste — browser parse เป็น grid คน×วัน แล้วส่ง raw
// (preview + commit ใช้ schema เดียวกัน; backend map+validate ฝั่งเดียวเป็น authority)
export const shiftRosterImportSchema = z.object({
  period: periodString,                  // YYYY-MM (เดือนที่จะลง)
  rows: z.array(z.object({
    key: z.string().min(1).max(120),     // emp_code หรือ ชื่อ (backend จับคู่เอง)
    cells: z.record(z.string(), z.string()),  // "1".."31" (วันที่ของเดือน) → ข้อความดิบ
  })).min(1).max(500),
});

// ดึงตารางเดือนก่อนมาเป็นฐานเดือนใหม่
export const rosterCopyPrevMonthSchema = z.object({
  period: periodString,                  // เดือนปลายทาง YYYY-MM
  overwrite: z.boolean().default(false), // ทับของเดิมในเดือนปลายทางไหม
});

// แพทเทิร์นหมุนเวร: sequence = ลำดับกะ 1 รอบ (uuid = กะ, null = วันหยุด)
export const rotationPatternSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(100),
  sequence: z.array(z.string().uuid().nullable()).min(1).max(60),
  active: z.boolean().default(true),
});

// generate ตารางหมุนของเดือน → เขียน shift_roster (ข้ามช่อง manual)
export const generateRotationSchema = z.object({
  period: periodString,
});

// คู่สลับตอบรับ/ปฏิเสธคำขอสลับเวร
export const swapRespondSchema = z.object({
  accept: z.boolean(),
});

export const createSwapRequestSchema = z.object({
  date: dateString,
  requester_emp_code: z.string().min(1).max(20),
  target_emp_code: z.string().min(1).max(20),
  reason: z.string().max(300).optional().or(z.literal('')),
});

export const decideSwapRequestSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  decision_note: z.string().max(300).optional().or(z.literal('')),
});

// === ขอย้ายวันหยุดตัวเอง (สลับวัน) ===
// original_date = วันหยุดเดิม → กลับมาทำงาน, target_date = วันใหม่ → หยุดแทน.
// ทั้งคู่ต้อง >= พรุ่งนี้ (validate วันล่วงหน้า ≥1 ทำในฝั่ง handler ตาม timezone ICT).
export const createShiftChangeRequestSchema = z.object({
  original_date: dateString,
  target_date: dateString,
  // กะที่จะเข้าทำงานในวันที่กลับมาทำงาน (วันหยุดเดิม). ว่าง = ใช้กะตามแพทเทิร์น/ค่าที่ resolve ได้.
  original_shift_id: z.string().uuid().optional().or(z.literal('')),
  reason: z.string().max(300).optional().or(z.literal('')),
});
export type CreateShiftChangeRequest = z.infer<typeof createShiftChangeRequestSchema>;

export const decideShiftChangeRequestSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejection_reason: z.string().max(300).optional().or(z.literal('')),
});
export type DecideShiftChangeRequest = z.infer<typeof decideShiftChangeRequestSchema>;

// === ขอเปลี่ยนกะการทำงาน (เปลี่ยนกะของตัวเองในวันหนึ่ง เช่น เข้า 08:00 → เข้า 12:00) ===
// date = วันที่ขอเปลี่ยน, target_shift_id = กะใหม่ที่จะเข้าทำงาน (บังคับเลือก).
// อนุมัติ 2 ชั้น + กระทันหัน + เขียนทับ roster เหมือน "ย้ายวันหยุด" (validate ICT ฝั่ง handler).
export const createChangeShiftRequestSchema = z.object({
  date: dateString,
  target_shift_id: z.string().uuid(),
  reason: z.string().max(300).optional().or(z.literal('')),
});
export type CreateChangeShiftRequest = z.infer<typeof createChangeShiftRequestSchema>;

// === รับรองเวลาเข้างาน (ขอแก้/รับรองเวลาเข้า-ออกงาน) — HR อนุมัติ ===
export const createAttendanceCorrectionSchema = z.object({
  date: dateString,
  kind: z.enum(['morning_in', 'evening_out']),
  requested_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'เวลาต้องเป็นรูปแบบ HH:mm'),
  reason: z.string().min(1, 'ต้องระบุเหตุผล').max(300),
});
export type CreateAttendanceCorrection = z.infer<typeof createAttendanceCorrectionSchema>;

// === HR แก้ไข/เพิ่มเวลาเข้า-ออกเองในทะเบียน (เทียบเท่า HumanSoft) ===
// time = null → ลบเวลารอบนั้นทิ้ง. reason บังคับทุกครั้ง เพราะเวลาที่ HR แก้เอง
// ไม่มีหลักฐานสแกน (รูป/พิกัด) — เหตุผลคือหลักฐานเดียวที่เหลือตอนตรวจย้อนหลัง.
export const hrAttendanceEntrySchema = z.object({
  emp_code: z.string().min(1, 'ต้องระบุรหัสพนักงาน'),
  date: dateString,
  entries: z
    .array(
      z.object({
        kind: z.enum(['morning_in', 'lunch_out', 'lunch_in', 'evening_out']),
        time: z
          .string()
          .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'เวลาต้องเป็นรูปแบบ HH:mm')
          .nullable(),
      }),
    )
    .min(1, 'ต้องระบุอย่างน้อย 1 รอบ')
    .max(4),
  reason: z.string().min(3, 'ต้องระบุเหตุผลที่แก้ไข').max(300),
});
export type HrAttendanceEntry = z.infer<typeof hrAttendanceEntrySchema>;

export const decideAttendanceCorrectionSchema = z.object({
  decision: z.enum(['approved', 'rejected']),
  rejection_reason: z.string().max(300).optional().or(z.literal('')),
});
export type DecideAttendanceCorrection = z.infer<typeof decideAttendanceCorrectionSchema>;

// === Phase 2B — Termination ===
export const TERMINATION_REASONS = ['resigned', 'terminated', 'terminated_for_cause', 'retired', 'contract_end'] as const;

export const terminationPreviewSchema = z.object({
  emp_code: z.string().min(1).max(20),
  effective_date: dateString,
  reason_code: z.enum(TERMINATION_REASONS),
  /** Days of notice the employer failed to give (0 if proper notice). */
  notice_shortfall_days: z.number().int().nonnegative().optional().default(0),
  /** Override accrued leave days if HR computes differently. */
  accrued_leave_days_override: z.number().nonnegative().optional(),
});

export const terminationCommitSchema = terminationPreviewSchema.extend({
  reason_note: z.string().max(500).optional().or(z.literal('')),
  /** Payroll period (YYYY-MM) to feed the payout into as adjustment_add. */
  feeds_payroll_period: periodString,
});

// === Phase 1C — Commission ===
export const COMMISSION_BASES = ['gross_sales', 'units_sold', 'gross_profit'] as const;

export const upsertCommissionPackageSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional().or(z.literal('')),
  basis: z.enum(COMMISSION_BASES),
  min_threshold: z.number().nonnegative().default(0),
  cap: z.number().nullable().optional(),
  floor: z.number().nullable().optional(),
  active: z.boolean().default(true),
  tiers: z
    .array(
      z.object({
        tier_order: z.number().int().min(1),
        min_amount: z.number().nonnegative(),
        max_amount: z.number().nullable().optional(),
        rate_percent: z.number().nullable().optional(),
        rate_fixed: z.number().nullable().optional(),
        notes: z.string().max(200).optional().or(z.literal('')),
      }),
    )
    .min(1),
});

export const upsertCommissionEligibilitySchema = z.object({
  id: z.string().uuid().optional(),
  emp_code: z.string().min(1).max(20),
  package_id: z.string().uuid(),
  start_date: dateString,
  end_date: dateString.optional().or(z.literal('')).nullable(),
  override_cap: z.number().nullable().optional(),
  override_floor: z.number().nullable().optional(),
  active: z.boolean().default(true),
});

export const createCommissionRunSchema = z.object({
  period_start: dateString,
  period_end: dateString,
});

export const upsertCommissionPerformanceSchema = z.object({
  emp_code: z.string().min(1).max(20),
  gross_sales: z.number().nonnegative().optional().default(0),
  units_sold: z.number().nonnegative().optional().default(0),
  gross_profit: z.number().nonnegative().optional().default(0),
  notes: z.string().max(500).optional().or(z.literal('')),
});

export const lockCommissionRunSchema = z.object({
  /** YYYY-MM — the payroll period that should receive these commission
   *  amounts as data_entries (entry_type='adjustment_add'). */
  feeds_payroll_period: periodString,
});

export const dryRunPayrollSchema = z.object({
  period: periodString,
  /** Per-emp tweaks to apply on top of currently saved data entries
   *  — only affects this in-memory recalc, no rows touched. */
  tweaks: z
    .array(
      z.object({
        emp_code: z.string(),
        additions: z.number().optional(),
        deductions: z.number().optional(),
      }),
    )
    .optional()
    .default([]),
});

export const onboardEmployeeSchema = z.object({
  // Optional: blank/omitted → server auto-generates the next running code
  // (EMP0001, EMP0002, …) scoped to the tenant.
  emp_code: z.string().trim().max(20).optional(),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.string().email(),
  department: z.string().min(1),
  position: z.string().min(1),
  start_date: dateString,
  base_salary: z.number().positive(),     // goes to Secret layer
  invite_role: z.enum(['EMPLOYEE', 'HR', 'SUPERVISOR', 'MANAGER']).default('EMPLOYEE'),
});

// Re-issue a pairing code for an EXISTING employee (link LINE later / re-link
// after revoke). Role defaults to EMPLOYEE when omitted.
export const reissuePairingSchema = z.object({
  role: z.enum(['EMPLOYEE', 'HR', 'SUPERVISOR', 'MANAGER']).default('EMPLOYEE'),
});
export type ReissuePairing = z.infer<typeof reissuePairingSchema>;

// === Drive config (Owner setup) ===
export const driveConfigureSchema = z.object({
  drive_root_id: z.string().min(10),     // Google Drive folder ID
});

// === Work policies (นโยบายการทำงาน — ข้อตกลงร่วมบริษัท↔พนักงาน) ===
export const upsertWorkPolicySchema = z.object({
  id: z.string().uuid().optional(),                 // present = update
  title: z.string().min(1).max(200),
  body: z.string().max(20000).default(''),
  category: z.enum(['time', 'ot', 'leave', 'holiday', 'general']).default('general'),
  sort_order: z.number().int().min(0).max(9999).optional(),
  published: z.boolean().optional(),
  doc_code: z.string().max(40).optional(),
  effective_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),  // YYYY-MM-DD
  announce: z.boolean().optional(),                 // ส่งแจ้งพนักงานซ้ำแม้เนื้อหาไม่เปลี่ยน
});
export type UpsertWorkPolicy = z.infer<typeof upsertWorkPolicySchema>;

export const ackPolicySchema = z.object({
  policy_id: z.string().uuid(),
  version: z.number().int().positive(),
});
export type AckPolicy = z.infer<typeof ackPolicySchema>;

// === จ่ายนอกรอบเงินเดือน (Off-cycle payouts — ค่าคอม/OT จ่ายแยก บันทึกว่าจ่ายแล้ว) ===
export const upsertPayoutTypeSchema = z.object({
  id: z.string().uuid().optional(),
  code: z.string().trim().min(1).max(40).regex(/^[a-z0-9_]+$/, 'code ใช้ a-z 0-9 _ เท่านั้น'),
  label: z.string().trim().min(1).max(100),
  kind: z.enum(['earning', 'deduction']).optional(),   // omit = earning (ของเดิมทั้งหมด)
  sort_order: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});
export type UpsertPayoutType = z.infer<typeof upsertPayoutTypeSchema>;

export const upsertPayoutRoundSchema = z.object({
  id: z.string().uuid().optional(),                 // present = update (draft only)
  title: z.string().trim().min(1).max(200),
  pay_date: dateString,
  wht_default_percent: z.number().min(0).max(99).optional(),
  note: z.string().max(2000).optional().or(z.literal('')),
  period: z.string().regex(/^\d{4}-\d{2}$/).optional(),        // งวดของสลิป YYYY-MM
  round_kind: z.enum(['salary', 'commission', 'other']).optional(),
});
export type UpsertPayoutRound = z.infer<typeof upsertPayoutRoundSchema>;

export const savePayoutItemsSchema = z.object({
  items: z.array(z.object({
    emp_code: z.string().trim().min(1).max(20),
    breakdown: z.record(z.string(), z.number()),    // {type_code: amount}
    wht_percent: z.number().min(0).max(99).optional(),  // omit = ใช้ default ของรอบ
    note: z.string().max(500).optional().or(z.literal('')),
  })).max(2000),
  remove_emp_codes: z.array(z.string().trim().min(1).max(20)).max(2000).optional(),
});
export type SavePayoutItems = z.infer<typeof savePayoutItemsSchema>;

// === เอกสารบริษัท (Company Document Vault) — docs/COMPANY_DOCS_SPEC.md ===
export const upsertCompanyDocumentSchema = z.object({
  id: z.string().uuid().optional(),          // มี = แก้, ไม่มี = สร้างใหม่
  label: z.string().trim().min(1, 'กรุณาระบุชื่อเอกสาร').max(200),
  note: z.string().max(1000).optional().or(z.literal('')),
  // ไฟล์ต้นฉบับจาก POST /upload (purpose=company) — ไม่ส่งมา = ไม่เปลี่ยนไฟล์เดิม (ตอนแก้)
  r2_path: z.string().trim().min(1).max(500).optional(),
  filename: z.string().max(255).optional().or(z.literal('')),
  mime: z.string().max(100).optional().or(z.literal('')),
  size_bytes: z.number().int().min(0).max(50_000_000).optional(),
  // '' = ยังไม่กรอก (undefined ที่ handler) ≠ ค่าว่างจริง — ห้าม default
  doc_date: dateString.optional().or(z.literal('')),
  expires_at: dateString.optional().or(z.literal('')),
  sort: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});
export type UpsertCompanyDocument = z.infer<typeof upsertCompanyDocumentSchema>;

export const createCompanyDocRequestSchema = z.object({
  doc_ids: z.array(z.string().uuid()).min(1, 'เลือกเอกสารอย่างน้อย 1 รายการ').max(50),
  purpose: z.string().trim().min(1, 'กรุณาระบุวัตถุประสงค์').max(300),
  recipient: z.string().trim().max(200).optional().or(z.literal('')),
});
export type CreateCompanyDocRequest = z.infer<typeof createCompanyDocRequestSchema>;

// ตัวเลือกการปั๊มสำเนา — snapshot ตอนอนุมัติ (§4.3 ของสเปก)
export const stampOptionsSchema = z.object({
  cross: z.boolean().optional(),
  cpos: z.union([z.literal(22), z.literal(40), z.literal(60)]).optional(),
  stamp: z.boolean().optional(),
  spos: z.enum(['all', 'first', 'last']).optional(),
  cert: z.boolean().optional(),
});
export type StampOptions = z.infer<typeof stampOptionsSchema>;

export const decideCompanyDocRequestSchema = z.object({
  action: z.enum(['approve', 'reject']),
  note: z.string().max(300).optional().or(z.literal('')),
  stamp_options: stampOptionsSchema.optional(),
});
export type DecideCompanyDocRequest = z.infer<typeof decideCompanyDocRequestSchema>;
